import { IdentityError } from '../network/IdentityService';
import type { CosmeticLoadout, IdentityService, MatchHistoryEntry, PlayerProfile } from '../network/IdentityService';
import { cosmeticCSSColor, cosmeticVisual } from '../game/content/cosmetics';

export class AccountFlow {
  private readonly dialog: HTMLDialogElement;
  private readonly loginIdentifier: HTMLInputElement;
  private readonly loginPassword: HTMLInputElement;
  private readonly loginError: HTMLElement;
  private readonly landing: HTMLElement;
  private readonly gate: HTMLElement;
  private readonly station: HTMLElement;
  private readonly stationTag: HTMLElement;
  private readonly accountSummary: HTMLElement;
  private readonly callTagSettings: HTMLElement;
  private readonly currentCallTag: HTMLElement;
  private readonly callTagState: HTMLElement;
  private readonly callTagToggle: HTMLButtonElement;
  private readonly callTagFields: HTMLElement;
  private readonly callTagCopy: HTMLElement;
  private readonly accountCallTag: HTMLInputElement;
  private readonly accountCurrentPassword: HTMLInputElement;
  private readonly callTagError: HTMLElement;
  private readonly callTagSubmit: HTMLButtonElement;
  private readonly cosmeticPicker: HTMLElement;
  private readonly cosmeticStatus: HTMLElement;
  private readonly matchHistory: HTMLOListElement;
  private readonly matchHistoryStatus: HTMLElement;
  private readonly registerOpenButton: HTMLButtonElement;
  private readonly loginButton: HTMLButtonElement;
  private readonly logoutButton: HTMLButtonElement;
  private readonly registrationDialog: HTMLDialogElement;
  private readonly registrationForm: HTMLFormElement;
  private readonly registrationCallTag: HTMLInputElement;
  private readonly registrationEmail: HTMLInputElement;
  private readonly registrationPassword: HTMLInputElement;
  private readonly registrationConfirm: HTMLInputElement;
  private readonly registrationError: HTMLElement;
  private readonly registrationSubmit: HTMLButtonElement;
  private pending = false;
  private callTagPending = false;
  private callTagEditorOpen = false;
  private callTagOwnerID = '';
  private callTagRestrictedPrompted = false;
  private callTagNotice = '';
  private cosmeticPending = false;
  private cosmeticOwnerID = '';
  private cosmeticLoadout: CosmeticLoadout | null = null;
  private matchHistoryPending = false;
  private matchHistoryOwnerID = '';
  private matchHistoryMatchesPlayed = -1;
  private matchHistoryEntries: MatchHistoryEntry[] | null = null;

  public constructor(root: HTMLElement, private readonly identity: IdentityService) {
    this.dialog = required(root, '[data-account]');
    this.loginIdentifier = required(root, '[data-auth-identifier]');
    this.loginPassword = required(root, '[data-auth-password]');
    this.loginError = required(root, '[data-auth-error]');
    this.landing = required(root, '[data-landing]');
    this.gate = required(root, '[data-station-auth]');
    this.station = required(root, '[data-station]');
    this.stationTag = required(root, '[data-station-call-tag]');
    this.accountSummary = required(root, '[data-account-summary]');
    this.callTagSettings = required(root, '[data-call-tag-settings]');
    this.currentCallTag = required(root, '[data-current-call-tag]');
    this.callTagState = required(root, '[data-call-tag-state]');
    this.callTagToggle = required(root, '[data-call-tag-toggle]');
    this.callTagFields = required(root, '[data-call-tag-fields]');
    this.callTagCopy = required(root, '[data-call-tag-copy]');
    this.accountCallTag = required(root, '[data-account-call-tag]');
    this.accountCurrentPassword = required(root, '[data-account-current-password]');
    this.callTagError = required(root, '[data-call-tag-error]');
    this.callTagSubmit = required(root, '[data-call-tag-submit]');
    this.cosmeticPicker = required(root, '[data-cosmetic-picker]');
    this.cosmeticStatus = required(root, '[data-cosmetic-status]');
    this.matchHistory = required(root, '[data-match-history]');
    this.matchHistoryStatus = required(root, '[data-match-history-status]');
    this.registerOpenButton = required(root, '[data-auth-register-open]');
    this.loginButton = required(root, '[data-auth-login]');
    this.logoutButton = required(root, '[data-account-logout]');
    this.registrationDialog = required(root, '[data-registration]');
    this.registrationForm = required(root, '[data-registration-form]');
    this.registrationCallTag = required(root, '[data-registration-call-tag]');
    this.registrationEmail = required(root, '[data-registration-email]');
    this.registrationPassword = required(root, '[data-registration-password]');
    this.registrationConfirm = required(root, '[data-registration-confirm]');
    this.registrationError = required(root, '[data-registration-error]');
    this.registrationSubmit = required(root, '[data-registration-submit]');

    for (const button of root.querySelectorAll<HTMLButtonElement>('[data-account-open]')) {
      button.addEventListener('click', this.open);
    }
    this.registerOpenButton.addEventListener('click', this.openRegistration);
    required<HTMLButtonElement>(root, '[data-registration-close]').addEventListener('click', this.closeRegistration);
    this.registrationDialog.addEventListener('cancel', (event) => {
      if (this.pending) event.preventDefault();
    });
    this.registrationDialog.addEventListener('close', () => {
      this.registrationPassword.value = '';
      this.registrationConfirm.value = '';
      this.registrationError.textContent = '';
    });
    this.registrationForm.addEventListener('submit', (event) => {
      event.preventDefault();
      void this.register();
    });
    this.loginButton.addEventListener('click', () => void this.login());
    this.loginPassword.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') void this.login();
    });
    this.logoutButton.addEventListener('click', () => void this.logout());
    this.callTagToggle.addEventListener('click', this.toggleCallTagEditor);
    this.callTagSubmit.addEventListener('click', () => void this.changeCallTag());
    this.accountCurrentPassword.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      void this.changeCallTag();
    });
    this.dialog.addEventListener('close', () => this.closeCallTagEditor());
    this.cosmeticPicker.addEventListener('click', (event) => {
      const button = (event.target as Element).closest<HTMLButtonElement>('[data-cosmetic]');
      if (button?.dataset.cosmetic) void this.equipCosmetic(button.dataset.cosmetic);
    });
    this.identity.subscribe(this.render);
  }

  public readonly open = (): void => {
    const profile = this.identity.current();
    if (profile?.guest === false) {
      this.dialog.showModal();
      void this.loadCosmetics();
      void this.loadMatchHistory();
      return;
    }
    this.loginIdentifier.focus();
  };

  private readonly openRegistration = (): void => {
    if (this.pending || this.registrationDialog.open) return;
    const identifier = this.loginIdentifier.value.trim();
    if (identifier && !identifier.includes('@')) this.registrationCallTag.value = identifier;
    this.registrationError.textContent = '';
    this.registrationDialog.showModal();
    this.registrationCallTag.focus();
  };

  private readonly closeRegistration = (): void => {
    if (!this.pending) this.registrationDialog.close();
  };

  private readonly render = (profile: PlayerProfile | null): void => {
    const isAccount = profile?.guest === false;
    this.landing.classList.toggle('is-station', isAccount);
    this.gate.hidden = isAccount;
    this.station.hidden = !isAccount;
    this.logoutButton.hidden = !isAccount;
    this.stationTag.textContent = isAccount ? profile.callTag ?? profile.displayName : 'UNREGISTERED';

    for (const bank of document.querySelectorAll<HTMLElement>('[data-banked-value]')) {
      bank.textContent = `₵${profile?.bankedValue ?? 0}`;
    }
    for (const label of document.querySelectorAll<HTMLElement>('[data-account-label]')) {
      label.textContent = isAccount ? profile.callTag?.toUpperCase() ?? profile.displayName.toUpperCase() : 'LOG IN';
    }

    const name = document.createElement('span');
    name.textContent = isAccount ? profile.callTag ?? profile.displayName : 'NO ACTIVE MANIFEST';
    const bank = document.createElement('b');
    bank.textContent = `₵${profile?.bankedValue ?? 0}`;
    const detail = document.createElement('small');
    detail.textContent = isAccount
      ? `SCORE ${profile.lifetimeScore} // MATCHES ${profile.matchesPlayed} // EXTRACTIONS ${profile.extractions}\nKNOCKOUTS ${profile.eliminations ?? 0} // FALLS ${profile.falls}`
      : 'CREATE AN ACCOUNT AT THE STATION';
    this.accountSummary.replaceChildren(name, bank, detail);

    if (profile?.id !== this.cosmeticOwnerID) {
      this.cosmeticOwnerID = profile?.id ?? '';
      this.cosmeticLoadout = null;
      this.cosmeticPicker.replaceChildren();
      this.cosmeticStatus.textContent = '';
    }
    if (profile?.id !== this.matchHistoryOwnerID) {
      this.matchHistoryOwnerID = profile?.id ?? '';
      this.matchHistoryEntries = null;
      this.matchHistoryMatchesPlayed = -1;
      this.matchHistoryStatus.textContent = '';
    }
    this.renderMatchHistory(profile);
    this.renderCosmetics(profile);
    this.renderCallTag(profile);

    if (isAccount && this.dialog.open && !this.matchHistoryPending && profile.matchesPlayed !== this.matchHistoryMatchesPlayed) {
      void this.loadMatchHistory();
    }

    if (!isAccount && profile?.guest && !this.loginIdentifier.value) this.loginIdentifier.value = profile.displayName;
    if (!isAccount && this.dialog.open) this.dialog.close();
    if (isAccount && this.registrationDialog.open) this.registrationDialog.close();
  };

  private readonly toggleCallTagEditor = (): void => {
    if (this.callTagPending || this.callTagToggle.disabled) return;
    this.callTagEditorOpen = !this.callTagEditorOpen;
    if (!this.callTagEditorOpen) {
      this.accountCurrentPassword.value = '';
      this.callTagError.textContent = '';
    }
    this.renderCallTag(this.identity.current());
    if (this.callTagEditorOpen) this.accountCallTag.focus();
  };

  private closeCallTagEditor(): void {
    if (this.callTagPending) return;
    this.callTagEditorOpen = false;
    this.callTagNotice = '';
    this.accountCurrentPassword.value = '';
    this.accountCallTag.value = '';
    this.callTagError.textContent = '';
    this.renderCallTag(this.identity.current());
  }

  private renderCallTag(profile: PlayerProfile | null): void {
    const isAccount = profile?.guest === false;
    const restricted = isAccount && profile.publicIdentityRestricted === true;
    const availableAt = profile?.callTagChangeAvailableAt ? Date.parse(profile.callTagChangeAvailableAt) : 0;
    const coolingDown = !restricted && Number.isFinite(availableAt) && availableAt > Date.now();
    if ((profile?.id ?? '') !== this.callTagOwnerID) {
      this.callTagOwnerID = profile?.id ?? '';
      this.callTagEditorOpen = false;
      this.callTagRestrictedPrompted = false;
      this.callTagNotice = '';
    }
    if (restricted && !this.callTagRestrictedPrompted) {
      this.callTagEditorOpen = true;
      this.callTagRestrictedPrompted = true;
    }
    if (!isAccount) this.callTagEditorOpen = false;

    this.callTagSettings.classList.toggle('is-restricted', restricted);
    this.currentCallTag.textContent = isAccount ? profile.callTag ?? profile.displayName : 'UNREGISTERED';
    this.callTagState.textContent = this.callTagNotice || (restricted
      ? 'PUBLIC SIGNAL LOCKED // RECLAIM NOW'
      : coolingDown
        ? `NEXT CHANGE ${new Date(availableAt).toLocaleDateString()}`
        : 'PASSWORD CONFIRMATION REQUIRED');
    this.callTagCopy.textContent = restricted
      ? 'Choose a compliant call tag to restore Station chat, friends, crews, and match identity immediately.'
      : 'Your call tag follows your friends, crews, and future runs. Changes are limited to once every 30 days.';
    this.callTagFields.hidden = !this.callTagEditorOpen;
    this.callTagToggle.textContent = this.callTagEditorOpen ? 'CANCEL' : restricted ? 'RECLAIM' : 'CHANGE';
    this.callTagToggle.disabled = this.callTagPending || !isAccount || coolingDown;
    this.callTagToggle.setAttribute('aria-expanded', String(this.callTagEditorOpen));
    this.accountCallTag.disabled = this.callTagPending;
    this.accountCurrentPassword.disabled = this.callTagPending;
    this.callTagSubmit.disabled = this.callTagPending;
  }

  private async changeCallTag(): Promise<void> {
    const profile = this.identity.current();
    if (this.callTagPending || profile?.guest !== false) return;
    const callTag = this.accountCallTag.value.trim();
    if (Array.from(callTag).length < 2 || Array.from(callTag).length > 20) {
      this.showCallTagError('CALL TAGS USE 2–20 CHARACTERS.');
      this.accountCallTag.focus();
      return;
    }
    if (callTag.toLocaleLowerCase() === (profile.callTag ?? profile.displayName).toLocaleLowerCase()) {
      this.showCallTagError('CHOOSE A DIFFERENT CALL TAG.');
      this.accountCallTag.focus();
      return;
    }
    if (this.accountCurrentPassword.value.length < 10) {
      this.showCallTagError('ENTER YOUR CURRENT PASSWORD.');
      this.accountCurrentPassword.focus();
      return;
    }
    this.callTagPending = true;
    this.callTagError.dataset.kind = '';
    this.callTagError.textContent = 'VERIFYING NEW PUBLIC SIGNAL…';
    this.renderCallTag(profile);
    try {
      const updated = await this.identity.changeCallTag(callTag, this.accountCurrentPassword.value);
      this.loginIdentifier.value = updated.callTag ?? updated.displayName;
      this.accountCallTag.value = '';
      this.accountCurrentPassword.value = '';
      this.callTagEditorOpen = false;
      this.callTagNotice = 'CALL TAG UPDATED ACROSS THE STATION';
      this.callTagError.textContent = '';
    } catch (error) {
      this.accountCurrentPassword.value = '';
      this.showCallTagError(error instanceof IdentityError ? error.message : 'The public signal failed.');
    } finally {
      this.callTagPending = false;
      this.renderCallTag(this.identity.current());
    }
  }

  private showCallTagError(message: string): void {
    this.callTagError.dataset.kind = 'error';
    this.callTagError.textContent = message.toUpperCase();
  }

  private async loadCosmetics(): Promise<void> {
    if (this.cosmeticPending || this.identity.current()?.guest !== false) return;
    const ownerID = this.identity.current()?.id;
    this.cosmeticPending = true;
    this.cosmeticStatus.dataset.danger = 'false';
    this.cosmeticStatus.textContent = 'READING SUIT LOCKER…';
    try {
      const loadout = await this.identity.cosmetics();
      if (this.identity.current()?.id !== ownerID) return;
      this.cosmeticLoadout = loadout;
      this.cosmeticStatus.textContent = 'SELECTED SUITS FOLLOW YOUR ACCOUNT INTO EVERY RUN.';
    } catch (error) {
      this.cosmeticStatus.dataset.danger = 'true';
      this.cosmeticStatus.textContent = error instanceof IdentityError ? error.message.toUpperCase() : 'SUIT LOCKER IS OFFLINE.';
    } finally {
      this.cosmeticPending = false;
      this.renderCosmetics(this.identity.current());
    }
  }

  private async loadMatchHistory(): Promise<void> {
    const profile = this.identity.current();
    if (this.matchHistoryPending || profile?.guest !== false) return;
    const ownerID = profile.id;
    const requestedMatches = profile.matchesPlayed;
    this.matchHistoryPending = true;
    this.matchHistoryStatus.dataset.danger = 'false';
    this.matchHistoryStatus.textContent = 'READING AUTHORITATIVE RUN LOG…';
    this.renderMatchHistory(profile);
    let loaded = false;
    try {
      const history = await this.identity.matchHistory();
      if (this.identity.current()?.id !== ownerID) return;
      this.matchHistoryEntries = history;
      this.matchHistoryMatchesPlayed = requestedMatches;
      loaded = true;
      this.matchHistoryStatus.textContent = history.length > 0
        ? `${history.length} SERVER-VERIFIED RUN${history.length === 1 ? '' : 'S'} // NEWEST FIRST`
        : 'COMPLETED RUNS WILL APPEAR HERE.';
    } catch (error) {
      if (this.identity.current()?.id !== ownerID) return;
      this.matchHistoryStatus.dataset.danger = 'true';
      this.matchHistoryStatus.textContent = error instanceof IdentityError ? error.message.toUpperCase() : 'RUN LOG IS OFFLINE.';
    } finally {
      this.matchHistoryPending = false;
      const current = this.identity.current();
      this.renderMatchHistory(current);
      if (loaded && current?.id === ownerID && current.guest === false && this.dialog.open &&
        this.matchHistoryEntries !== null && current.matchesPlayed !== this.matchHistoryMatchesPlayed) {
        void this.loadMatchHistory();
      }
    }
  }

  private renderMatchHistory(profile: PlayerProfile | null): void {
    if (profile?.guest !== false) {
      this.matchHistory.replaceChildren();
      return;
    }
    if (this.matchHistoryEntries === null) {
      const loading = document.createElement('li');
      loading.className = 'match-history__empty';
      loading.textContent = this.matchHistoryPending ? 'READING RUN LOG…' : 'OPEN MANIFEST TO LOAD RUNS.';
      this.matchHistory.replaceChildren(loading);
      return;
    }
    if (this.matchHistoryEntries.length === 0) {
      const empty = document.createElement('li');
      empty.className = 'match-history__empty';
      empty.textContent = 'NO COMPLETED RUNS YET // YOUR FIRST LINE IS WAITING.';
      this.matchHistory.replaceChildren(empty);
      return;
    }
    const rows = this.matchHistoryEntries.map((run) => {
      const row = document.createElement('li');
      row.className = `match-history__run is-${run.outcome}`;
      row.dataset.matchHistoryOutcome = run.outcome;

      const outcome = document.createElement('span');
      const label = document.createElement('b');
      label.textContent = run.outcome === 'extracted' ? 'EXTRACTED' : 'LINE CLOSED';
      const time = document.createElement('time');
      time.dateTime = run.completedAt;
      time.textContent = formatRunTime(run.completedAt);
      outcome.append(label, time);

      const detail = document.createElement('span');
      const route = document.createElement('b');
      route.textContent = `TRAIN ${Math.max(1, run.extractionStop)} // ×${run.multiplier.toFixed(2)}`;
      const combat = document.createElement('small');
      combat.textContent = `SCORE ${run.score} // KO ${run.eliminations} // FALLS ${run.falls}`;
      detail.append(route, combat);

      const value = document.createElement('strong');
      value.textContent = run.outcome === 'extracted' ? `+₵${run.bankedValue}` : `−₵${run.lostValue}`;
      row.append(outcome, detail, value);
      return row;
    });
    this.matchHistory.replaceChildren(...rows);
  }

  private async equipCosmetic(cosmetic: string): Promise<void> {
    const option = this.cosmeticLoadout?.options.find((candidate) => candidate.id === cosmetic);
    if (this.cosmeticPending || !option?.unlocked || cosmetic === this.cosmeticLoadout?.selected) return;
    this.cosmeticPending = true;
    this.cosmeticStatus.dataset.danger = 'false';
    this.cosmeticStatus.textContent = `TUNING ${option.label.toUpperCase()}…`;
    this.renderCosmetics(this.identity.current());
    try {
      const profile = await this.identity.equipCosmetic(cosmetic);
      if (this.cosmeticLoadout) this.cosmeticLoadout.selected = profile.cosmetic ?? cosmetic;
      this.cosmeticStatus.textContent = `${option.label.toUpperCase()} EQUIPPED // NEXT BOARDING`;
    } catch (error) {
      this.cosmeticStatus.dataset.danger = 'true';
      this.cosmeticStatus.textContent = error instanceof IdentityError ? error.message.toUpperCase() : 'SUIT SIGNAL FAILED.';
    } finally {
      this.cosmeticPending = false;
      this.renderCosmetics(this.identity.current());
    }
  }

  private renderCosmetics(profile: PlayerProfile | null): void {
    if (!this.cosmeticLoadout || profile?.guest !== false) return;
    const selected = profile.cosmetic ?? this.cosmeticLoadout.selected;
    const cards = this.cosmeticLoadout.options.map((option) => {
      const button = document.createElement('button');
      const isSelected = option.id === selected;
      button.type = 'button';
      button.dataset.cosmetic = option.id;
      button.className = `cosmetic-card${isSelected ? ' is-equipped' : ''}${option.unlocked ? '' : ' is-locked'}`;
      button.disabled = this.cosmeticPending || !option.unlocked || isSelected;
      button.setAttribute('aria-pressed', String(isSelected));

      const visual = cosmeticVisual(option.id);
      const swatch = document.createElement('i');
      swatch.className = 'cosmetic-swatch';
      if (visual) {
        swatch.style.setProperty('--cosmetic-suit', cosmeticCSSColor(visual.suit));
        swatch.style.setProperty('--cosmetic-accent', cosmeticCSSColor(visual.accent));
      }
      const copy = document.createElement('span');
      const label = document.createElement('b');
      label.textContent = option.label;
      const description = document.createElement('small');
      description.textContent = option.description;
      copy.append(label, description);
      const state = document.createElement('em');
      state.textContent = isSelected ? 'EQUIPPED' : option.unlocked ? 'AVAILABLE' : `BANK ₵${option.bankRequired}`;
      button.append(swatch, copy, state);
      return button;
    });
    this.cosmeticPicker.replaceChildren(...cards);
  }

  private async login(): Promise<void> {
    if (this.pending) return;
    const identifier = this.loginIdentifier.value.trim();
    if (!identifier) {
      this.showLoginError('ENTER YOUR CALL TAG OR EMAIL.');
      this.loginIdentifier.focus();
      return;
    }
    this.setPending(true);
    this.loginError.textContent = 'CHECKING MANIFEST…';
    try {
      await this.identity.login(identifier, this.loginPassword.value);
      this.loginPassword.value = '';
      this.loginError.textContent = '';
    } catch (error) {
      this.showLoginError(error instanceof IdentityError ? error.message : 'The account signal failed.');
    } finally {
      this.setPending(false);
    }
  }

  private async register(): Promise<void> {
    if (this.pending) return;
    const callTag = this.registrationCallTag.value.trim();
    const email = this.registrationEmail.value.trim();
    if (Array.from(callTag).length < 2 || Array.from(callTag).length > 20) {
      this.showRegistrationError('CALL TAGS USE 2–20 CHARACTERS.');
      this.registrationCallTag.focus();
      return;
    }
    if (!this.registrationEmail.validity.valid || !email) {
      this.showRegistrationError('ENTER A VALID EMAIL ADDRESS.');
      this.registrationEmail.focus();
      return;
    }
    if (this.registrationPassword.value.length < 10) {
      this.showRegistrationError('PASSWORDS USE AT LEAST 10 CHARACTERS.');
      this.registrationPassword.focus();
      return;
    }
    if (this.registrationPassword.value !== this.registrationConfirm.value) {
      this.showRegistrationError('PASSWORDS DO NOT MATCH.');
      this.registrationConfirm.focus();
      return;
    }
    this.setPending(true);
    this.registrationError.textContent = 'CREATING MANIFEST…';
    try {
      await this.identity.register(callTag, email, this.registrationPassword.value);
      this.loginIdentifier.value = callTag;
      this.loginPassword.value = '';
      this.registrationForm.reset();
      this.registrationError.textContent = '';
    } catch (error) {
      this.showRegistrationError(error instanceof IdentityError ? error.message : 'The account signal failed.');
    } finally {
      this.setPending(false);
    }
  }

  private async logout(): Promise<void> {
    if (this.pending) return;
    this.pending = true;
    await this.identity.logout();
    this.pending = false;
    this.dialog.close();
    this.loginIdentifier.focus();
  }

  private setPending(pending: boolean): void {
    this.pending = pending;
    this.registerOpenButton.disabled = pending;
    this.loginButton.disabled = pending;
    this.registrationSubmit.disabled = pending;
  }

  private showLoginError(message: string): void {
    this.loginError.textContent = message.toUpperCase();
  }

  private showRegistrationError(message: string): void {
    this.registrationError.textContent = message.toUpperCase();
  }
}

function required<T extends Element>(root: HTMLElement, selector: string): T {
  const element = root.querySelector<T>(selector);
  if (!element) throw new Error(`Missing account element: ${selector}`);
  return element;
}

function formatRunTime(value: string): string {
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) return 'TIME UNKNOWN';
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(timestamp).toUpperCase();
}
